import { useCallback, useRef } from 'react'

const DEFAULT_FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Returns visible, focusable elements within a root container.
 * @param {ParentNode|null} root
 * @param {string} selector
 * @returns {HTMLElement[]}
 */
export const getVisibleFocusableElements = (root, selector = DEFAULT_FOCUSABLE_SELECTOR) => {
  if (!root) return []

  return Array.from(root.querySelectorAll(selector)).filter((el) => {
    return el instanceof HTMLElement && el.offsetParent !== null
  })
}

/**
 * Determines if an element should keep native arrow-key behavior.
 * @param {Element|null} element
 * @returns {boolean}
 */
export const isKeyboardEditableElement = (element) => {
  if (!(element instanceof HTMLElement)) return false

  return (
    element.matches('input:not([type="button"]):not([type="submit"]):not([type="reset"]), textarea, select') ||
    element.isContentEditable
  )
}

/**
 * Stores and restores the most recently focused element in a region.
 * @returns {{onFocusCapture: (e: React.FocusEvent) => void, focusPreferred: (focusables: HTMLElement[]) => boolean, lastFocusedRef: React.MutableRefObject<HTMLElement|null>}}
 */
const useFocusMemory = () => {
  const lastFocusedRef = useRef(null)

  const onFocusCapture = useCallback((e) => {
    if (e.target instanceof HTMLElement) {
      lastFocusedRef.current = e.target
    }
  }, [])

  const focusPreferred = useCallback((focusables) => {
    if (!focusables.length) return false

    const preferred =
      lastFocusedRef.current && focusables.includes(lastFocusedRef.current)
        ? lastFocusedRef.current
        : focusables[0]

    preferred?.focus()
    return !!preferred
  }, [])

  return {
    onFocusCapture,
    focusPreferred,
    lastFocusedRef,
  }
}

export default useFocusMemory
